import { useEffect, useState } from "react";
import {
  createService,
  deleteServices,
  getServices,
  setSelectDeleteService,
  setSelectedService,
  addOptimisticlly,
  saveServicePricesThunk,
} from "./services.slice";
import { useAppDispatch, useAppSelector } from "@/lib/store";
import { Service } from "@/models/services.model";
import { priceChangesOf, pricingOf, type ServicePricing } from "@/repositories/service-records";
import { toast } from "sonner";

function groupByParent(services: Service[]) {
  const map = new Map();
  for (const service of services) {
    map.set(service.id, { ...service, children: [] });
  }
  const grouped = [];
  for (const service of services) {
    if (service.parent) {
      const parent = map.get(service.parent);
      if (parent) {
        parent.children.push(map.get(service.id));
      }
    } else {
      grouped.push(map.get(service.id));
    }
  }

  return grouped;
}

export function useServices() {
  const dispatch = useAppDispatch();
  const { list, loading, total, selectedService, selectDeleteService } = useAppSelector((state) => state.services);

  // Table Accordion
  const [openedServices, setOpenedServices] = useState([] as string[]);
  // Dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [openSubDialog, setOpenSubDialog] = useState(false);
  const [delDialog, setDelDialog] = useState(false);
  // manage dropdown
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  // Edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [servicePrices, setServicePrices] = useState<Record<string, ServicePricing>>({});

  function handleToggleService(service: string) {
    if (openedServices.includes(service)) {
      setOpenedServices(openedServices.filter((s) => s !== service));
    } else {
      setOpenedServices([...openedServices, service]);
    }
  }

  function handleExpandAll() {
    const allParentServiceIds = groupByParent(list).map((service) => service.id);
    setOpenedServices(allParentServiceIds);
  }

  function handleCollapseAll() {
    setOpenedServices([]);
  }
  function loadServices() {
    dispatch(getServices());
  }
  useEffect(() => {
    loadServices();
  }, []);

  useEffect(() => {
    const initOpened = list.length > 0 ? groupByParent(list)[0].id : "";
    setOpenedServices([initOpened]);
  }, [list.length]);

  useEffect(() => {
    if (openDialog || openSubDialog || delDialog) {
      setOpenDropdownId(null);
    }
  }, [openDialog, openSubDialog, delDialog]);

  async function handleCreate(values: Partial<Service>) {
    try {
      const data = await dispatch(createService(values)).unwrap();
      setOpenDialog(false);
      dispatch(addOptimisticlly(data));
      toast.success("Success", {
        description: "Services successfully added",
      });
    } catch {
      toast.error("Oops!!", {
        description: "Something went wrong while adding services",
      });
    }
  }

  async function handleCreateSub(values: Partial<Service>) {
    try {
      const data = await dispatch(createService({ ...values, parent: selectedService })).unwrap();
      setOpenSubDialog(false);
      dispatch(addOptimisticlly(data));
      toast.success("Success", {
        description: "Services successfully added",
      });
    } catch {
      toast.error("Oops!!", {
        description: "Something went wrong while adding services",
      });
    }
  }

  function handleMenuService(id: string) {
    dispatch(setSelectedService(id));
    setOpenSubDialog(true);
  }

  function handleDeleteServiceModal(id: string) {
    dispatch(setSelectDeleteService(id));
    setDelDialog(true);
  }

  async function handleDelete() {
    try {
      if (selectDeleteService) {
        await dispatch(deleteServices(selectDeleteService)).unwrap();
        dispatch(setSelectDeleteService(null));
        setDelDialog(false);
        loadServices();
        toast.success("Success", {
          description: "Services successfully deleted",
        });
      }
    } catch {
      toast.error("Oops!!", {
        description: "Something went wrong while deleting services ",
      });
    }
  }

  function handleClose() {
    dispatch(setSelectDeleteService(null));
    dispatch(setSelectedService(null));
  }

  function handleEditMode() {
    setIsEditMode(true);
    // Initialize service prices with current values
    const prices: Record<string, ServicePricing> = {};
    const groupedServices = groupByParent(list);
    groupedServices.forEach((service) => {
      prices[service.id] = pricingOf(service);
      if (service.children) {
        service.children.forEach((child: Service) => {
          prices[child.id] = pricingOf(child);
        });
      }
    });
    setServicePrices(prices);
  }

  function handleCancelEdit() {
    setIsEditMode(false);
    setServicePrices({});
  }

  function handlePriceChange<Field extends keyof ServicePricing>(serviceId: string, field: Field, value: ServicePricing[Field]) {
    setServicePrices((prev) => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        [field]: value,
      },
    }));
  }

  async function handleSaveChanges() {
    try {
      const changes = priceChangesOf(list, servicePrices);
      if (changes.length > 0) await dispatch(saveServicePricesThunk(changes)).unwrap();
      setIsEditMode(false);
      setServicePrices({});
      loadServices();
      toast.success("Success", {
        description: "Services successfully updated",
      });
    } catch {
      toast.error("Oops!!", {
        description: "Something went wrong while updating services",
      });
    }
  }

  return {
    list: groupByParent(list),
    loading,
    total,
    handleToggleService,
    openedServices,
    handleExpandAll,
    handleCollapseAll,
    isEditMode,
    handleEditMode,
    handleCancelEdit,
    handleSaveChanges,
    servicePrices,
    handlePriceChange,
    openDialog,
    setOpenDialog,
    handleCreate,
    openSubDialog,
    setOpenSubDialog,
    handleMenuService,
    handleCreateSub,
    delDialog,
    setDelDialog,
    handleDeleteServiceModal,
    handleDelete,
    handleClose,
    openDropdownId,
    setOpenDropdownId,
  };
}
